import { useQuery } from '@tanstack/react-query'
import { api } from '@personic/shared'

export default function EmployeeDashboard() {
  const { data: stats, isLoading, isError } = useQuery({
    queryKey: ['employee-stats'],
    queryFn: async () => {
      const [usageRes, tenantsRes] = await Promise.all([
        api.get('/usage/all-tenants'),
        api.get('/tenants'),
      ])
      return {
        usage: usageRes.data,
        tenants: tenantsRes.data,
      }
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Failed to load dashboard data.</div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Stats Grid */}
      <div className="grid md:grid-cols-4 gap-6">
        <StatCard
          label="Total Tenants"
          value={stats?.tenants?.length || 0}
          icon="🏢"
        />
        <StatCard
          label="Total Requests (30d)"
          value={stats?.usage?.summary?.total_requests || 0}
          icon="📊"
        />
        <StatCard
          label="Success Rate"
          value={`${stats?.usage?.summary?.success_rate || 0}%`}
          icon="✅"
        />
        <StatCard
          label="Avg Response Time"
          value={`${stats?.usage?.summary?.avg_response_time_ms || 0}ms`}
          icon="⚡"
        />
      </div>

      {/* Recent Activity */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">System Overview</h2>
        </div>
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Tenants by Plan */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-4">Tenants by Plan</h3>
              <div className="space-y-3">
                {['enterprise', 'pro', 'trial'].map((plan) => {
                  const count = stats?.tenants?.filter(
                    (t: any) => t.plan === plan
                  ).length || 0
                  return (
                    <div key={plan} className="flex items-center justify-between">
                      <span className="text-gray-700 capitalize">{plan}</span>
                      <span className="font-semibold text-gray-900">{count}</span>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Top Endpoints */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-4">Top Endpoints</h3>
              <div className="space-y-3">
                {(stats?.usage?.endpoints || []).slice(0, 5).map((ep: any) => (
                  <div key={ep.endpoint} className="flex items-center justify-between">
                    <span className="text-gray-700 truncate max-w-[200px]">{ep.endpoint}</span>
                    <span className="font-semibold text-gray-900">{ep.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function StatCard({
  label,
  value,
  icon,
}: {
  label: string
  value: string | number
  icon: string
}) {
  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className="text-3xl">{icon}</div>
      </div>
    </div>
  )
}
